import { AxiosInstance } from 'axios'
import dayjs from 'dayjs'
import { InjectionKey, reactive } from 'vue'
import { ScheduleApi } from '@/api/common/schedule'
import { ComputableObject, ApiError } from '@/types/global'
import {
  IScheduleGetDailyScheduleApiDetail,
  IScheduleGetMonthlyScheduleApiDetail,
  IScheduleStore,
} from '@/types/common/schedule'
import { useCommonApiError } from '@/hooks/error/common'

type Dayjs = typeof dayjs
export class ScheduleStore {
  api: ScheduleApi
  dayjs: Dayjs
  constructor(axios: AxiosInstance, dayjs: Dayjs) {
    this.api = new ScheduleApi(axios)
    this.dayjs = dayjs
  }

  public state: IScheduleStore = reactive<ComputableObject<IScheduleStore>>({
    dailySchedule: [],
    monthlySchedule: [],
    current: {
      year: 1900,
      month: 1,
      day: 1,
      isHoliday: false,
    },
    currentPrev: null,
    currentNext: null,
    schedule: {
      photographerUserId: 0,
      photographerSlug: '',
      price: 0,
      holidayPrice: 0,
      scheduleOpenPeriod: 0,
      loadDate: '',
      loadScheduleType: 'monthly',
      scheduleMaxNum: 0,
    },
  })

  /**
   * dailyScheduleをセット
   * @param dailySchedule
   */
  public setDailySchedule(schedule: IScheduleGetDailyScheduleApiDetail['schedule']): void {
    const value: IScheduleStore['dailySchedule'] = []
    for (const [time, item] of Object.entries(schedule)) {
      value.push({
        time: time,
        scheduleId: item.scheduleId ?? 0,
        acceptStatus: item.acceptStatus === false ? '' : item.acceptStatus,
      })
    }
    this.state.dailySchedule = value
  }

  /**
   * monthlyScheduleをセット
   * @param monthlySchedule
   */
  public setMonthlySchedule(schedule: IScheduleGetMonthlyScheduleApiDetail['schedule']): void {
    const value: IScheduleStore['monthlySchedule'] = []
    for (const [date, item] of Object.entries(schedule)) {
      value.push({
        date: date,
        freeFlag: item.freeFlag,
        holidayFlag: item.holidayFlag,
      })
    }
    this.state.monthlySchedule = value
  }

  /**
   * currentをセット
   * @param year
   * @param month
   * @param day
   * @param isHoliday
   */
  public setCurrent(year: number, month: number, day = 1, isHoliday = false): void {
    this.state.current = {
      year: year,
      month: month,
      day: day,
      isHoliday: isHoliday,
    }
  }

  /**
   * currentPrevをセット
   * @param date YYYY-MM-DD
   */
  public setCurrentPrev(date: string | false = ''): void {
    if (!date) {
      this.state.currentPrev = null
      return
    }
    const split = date.split('-')
    this.state.currentPrev = {
      year: Number(split[0]),
      month: Number(split[1]),
      day: Number(split[2]),
    }
  }

  /**
   * currentNextをセット
   * @param date YYYY-MM-DD
   */
  public setCurrentNext(date: string | false = ''): void {
    if (!date) {
      this.state.currentNext = null
      return
    }
    const split = date.split('-')
    this.state.currentNext = {
      year: Number(split[0]),
      month: Number(split[1]),
      day: Number(split[2]),
    }
  }

  /**
   * [API] 対象のフォトグラファーの指定日のスケジュールを取得
   * @param year 年
   * @param month 月
   * @param day 日
   */
  public async getDailySchedule(year: number, month: number, day: number): Promise<IScheduleGetDailyScheduleApiDetail> {
    const data = await this.api.getDailySchedule(this.state.schedule.photographerUserId, year, month, day)

    this.setDailySchedule(data.schedule)
    this.setCurrent(year, month, day, data.holidayFlag)
    this.setCurrentPrev(data.prev)
    this.setCurrentNext(data.next)

    // 表示タイプの切り替え
    this.state.schedule.loadScheduleType = 'daily'

    return data
  }

  /**
   * [API] 対象のフォトグラファーの指定月のスケジュールを取得
   * @param year 年
   * @param month 月
   */
  public async getMonthlySchedule(year: number, month: number): Promise<IScheduleGetMonthlyScheduleApiDetail> {
    const data = await this.api.getMonthlySchedule(this.state.schedule.photographerUserId, year, month)

    this.setMonthlySchedule(data.schedule)
    this.setCurrent(year, month)

    // 表示タイプの切り替え
    this.state.schedule.loadScheduleType = 'monthly'

    const current = this.dayjs(`${year}-${month}`)
    if (this.dayjs().tz().add(this.state.schedule.scheduleOpenPeriod, 'day').isSame(`${year}-${month}`, 'month')) {
      this.setCurrentPrev()
    } else {
      this.setCurrentPrev(current.subtract(1, 'month').format('YYYY-MM-01'))
    }

    const oneYearLater = this.dayjs().tz().add(1, 'year')
    if (oneYearLater.isAfter(current, 'month')) {
      this.setCurrentNext(current.add(1, 'month').format('YYYY-MM-01'))
    } else {
      this.setCurrentNext()
    }

    return data
  }

  /**
   * スケジュール初期情報取得API
   *
   * @param photographerId フォトグラファーID
   * @param date yyyy-mm-dd
   */
  public async getDefaultSchedule(photographerId: number, date?: string): Promise<void> {
    await this.api
      .getDefaultSchedule(photographerId, date)
      .then((response) => {
        this.state.schedule = response
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }
}

export const scheduleStoreKey: InjectionKey<ScheduleStore> = Symbol('scheduleStoreKey')
